// src/components/ManualSaleForm.tsx

import React, { useMemo, useState, useEffect } from "react";
import { Loader2, X, ChevronDown, ChevronUp } from "lucide-react";
import { showSuccess, showError } from "../utils/showToast";
import { API_BASE_URL } from "../config/api"; 

interface ManualSaleFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    memberUuid?: string;
}

// ===== Helpers =====
const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
};

const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
};

const normalizeIndianPhone = (raw: string): string => {
    let clean = (raw || "").trim().replace(/\s+/g, "");
    if (clean.startsWith("+91")) clean = clean.slice(3);
    else if (clean.startsWith("91") && clean.length === 12) clean = clean.slice(2);
    return clean;
};

const isValidIndianPhone = (phone10: string) => /^[6-9]\d{9}$/.test(phone10);
const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const INPUT =
    "w-full px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-blue-600 transition-colors";

const LABEL =
    "block text-xs text-gray-500 font-medium uppercase tracking-wide mb-1";

// ===== Form State =====
type FormState = {
    uniqueId: string;
    productName: string;
    phoneNo: string;
    carePrice: string;
    transactionDate: string;
    brand: string;
    color: string;
    productPrice: string;
    customerName: string;
    email: string;
    address: string;
    product: string;
    size: string;
    sold: string;
    gender: string;
    sizeType: string;
};

const ManualSaleForm: React.FC<ManualSaleFormProps> = ({
    isOpen,
    onClose,
    onSuccess,
    memberUuid: memberUuidProp,
}) => {
    const initialState: FormState = useMemo(
        () => ({
            uniqueId: "",
            productName: "",
            phoneNo: "",
            carePrice: "",
            transactionDate: getTodayDate(),
            brand: "",
            color: "",
            productPrice: "",
            customerName: "",
            email: "",
            address: "",
            product: "",
            size: "",
            sold: "true",
            gender: "-",
            sizeType: "",
        }),
        []
    );

    const [formData, setFormData] = useState<FormState>(initialState);
    const [showOptional, setShowOptional] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const h = (e: KeyboardEvent) => {
            if (e.key === "Escape") resetAndClose();
        };
        if (isOpen) {
            window.addEventListener("keydown", h);
            document.body.style.overflow = "hidden";
        }
        return () => {
            window.removeEventListener("keydown", h);
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    const setField = (name: keyof FormState, value: string) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
        setError("");
    };

    const resetAndClose = () => {
        setFormData(initialState);
        setShowOptional(false);
        setError("");
        onClose();
    };

    const validate = (): {
        ok: boolean;
        message?: string;
        cleanedPhone?: string;
    } => {
        if (
            !formData.uniqueId.trim() ||
            !formData.productName.trim() ||
            !formData.phoneNo.trim() ||
            !formData.carePrice.trim() ||
            !formData.transactionDate
        ) {
            return { ok: false, message: "Please fill in all required fields." };
        }

        const cleanedPhone = normalizeIndianPhone(formData.phoneNo);
        if (!isValidIndianPhone(cleanedPhone)) {
            return {
                ok: false,
                message:
                    "Invalid phone number. Must be 10 digits starting with 6-9.",
            };
        }

        const selectedDate = new Date(formData.transactionDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        if (selectedDate > today) {
            return {
                ok: false,
                message: "Purchase Date cannot be in the future.",
            };
        }

        const carePrice = parseFloat(formData.carePrice);
        if (Number.isNaN(carePrice) || carePrice <= 0) {
            return {
                ok: false,
                message: "Care+ Price must be a valid number greater than 0.",
            };
        }

        if (!formData.brand.trim())
            return { ok: false, message: "Brand is required." };
        if (!formData.color.trim())
            return { ok: false, message: "Color is required." };
        if (!formData.productPrice.trim())
            return { ok: false, message: "Product Price is required." };

        const productPrice = parseFloat(formData.productPrice);
        if (Number.isNaN(productPrice) || productPrice < 0) {
            return {
                ok: false,
                message: "Product Price must be a valid number.",
            };
        }

        if (formData.email.trim() && !isValidEmail(formData.email)) {
            return { ok: false, message: "Invalid email format." };
        }

        if (formData.size.trim()) {
            const sizeNum = Number(formData.size);
            if (
                Number.isNaN(sizeNum) ||
                !Number.isFinite(sizeNum) ||
                sizeNum < 0
            ) {
                return {
                    ok: false,
                    message: "Size must be a valid number (or leave empty).",
                };
            }
        }

        return { ok: true, cleanedPhone };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        const memberUuid =
            memberUuidProp || localStorage.getItem("memberUuid");
        if (!memberUuid) {
            setError("Member UUID not found. Please log in again.");
            return;
        }

        const v = validate();
        if (!v.ok) {
            setError(v.message || "Validation failed.");
            return;
        }

        setLoading(true);
        try {
            const payload: any = {
                uniqueId: formData.uniqueId.trim(),
                productName: formData.productName.trim(),
                phoneNo: v.cleanedPhone!,
                carePrice: formData.carePrice.trim(),
                transactionDate: formatDateForAPI(formData.transactionDate),
                brand: formData.brand.trim(),
                color: formData.color.trim(),
                productPrice: formData.productPrice.trim(),
                memberUuid,
                currencyCode: "INR",
                ...(formData.customerName.trim()
                    ? { customerName: formData.customerName.trim() }
                    : {}),
                ...(formData.email.trim()
                    ? { email: formData.email.trim() }
                    : {}),
                ...(formData.address.trim()
                    ? { address: formData.address.trim() }
                    : {}),
                ...(formData.product.trim()
                    ? { product: formData.product.trim() }
                    : {}),
                ...(formData.size.trim()
                    ? { size: Number(formData.size) }
                    : {}),
                ...(formData.gender ? { gender: formData.gender } : {}),
                ...(formData.sizeType
                    ? { sizeType: formData.sizeType }
                    : {}),
                sold: formData.sold === "true",
            };

            const response = await fetch(
                `${API_BASE_URL}/api/sale/manual`, // 👈 changed
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-member-uuid": memberUuid,
                    },
                    body: JSON.stringify(payload),
                }
            );

            const result = await response.json();
            if (!response.ok)
                throw new Error(result?.error || "Failed to create sale");

            showSuccess("Care+ order created successfully!");
            setFormData(initialState);
            setShowOptional(false);
            onSuccess();
            onClose();
        } catch (err: any) {
            showError(err?.message || "Failed to create order");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={resetAndClose}
        >
            <div
                className="relative w-full max-w-2xl bg-[#111111] border border-[#2d2d2d] rounded-lg max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#2d2d2d] flex-shrink-0">
                    <div>
                        <h2 className="text-sm font-semibold text-white">
                            Create Care+ Order
                        </h2>
                        <p className="text-xs text-gray-600 mt-0.5">
                            Fields marked{" "}
                            <span className="text-red-400">*</span> are required
                        </p>
                    </div>
                    <button
                        onClick={resetAndClose}
                        className="p-1.5 text-gray-500 hover:text-gray-200 rounded transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="overflow-y-auto flex-1 p-5">
                    {/* Error Banner */}
                    {error && (
                        <div className="mb-4 px-3 py-2.5 bg-red-500/5 border border-red-500/20 rounded text-red-400 text-xs flex items-start gap-2">
                            <svg
                                className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* ── Required Fields ── */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Order No (uniqueId) */}
                            <div>
                                <label className={LABEL}>
                                    Order No{" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <input
                                    className={INPUT}
                                    type="text"
                                    value={formData.uniqueId}
                                    onChange={(e) =>
                                        setField("uniqueId", e.target.value)
                                    }
                                    placeholder="#1001"
                                    required
                                />
                            </div>

                            {/* Purchase Date (transactionDate) */}
                            <div>
                                <label className={LABEL}>
                                    Purchase Date{" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <input
                                    className={INPUT}
                                    type="date"
                                    value={formData.transactionDate}
                                    onChange={(e) =>
                                        setField(
                                            "transactionDate",
                                            e.target.value
                                        )
                                    }
                                    max={getTodayDate()}
                                    required
                                />
                                <p className="text-[10px] text-gray-600 mt-1">
                                    {formatDateForDisplay(
                                        formData.transactionDate
                                    )}
                                </p>
                            </div>

                            {/* Product Name (productName) */}
                            <div className="sm:col-span-2">
                                <label className={LABEL}>
                                    Product Name{" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <input
                                    className={INPUT}
                                    type="text"
                                    value={formData.productName}
                                    onChange={(e) =>
                                        setField("productName", e.target.value)
                                    }
                                    placeholder="Court Vision Low - 6 / Blue"
                                    required
                                />
                            </div>

                            {/* Phone Number (phoneNo) */}
                            <div>
                                <label className={LABEL}>
                                    Phone Number{" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 text-xs font-mono">
                                        +91
                                    </span>
                                    <input
                                        className={`${INPUT} pl-11`}
                                        type="tel"
                                        value={formData.phoneNo}
                                        onChange={(e) =>
                                            setField("phoneNo", e.target.value)
                                        }
                                        placeholder="9876543210"
                                        maxLength={13}
                                        required
                                    />
                                </div>
                                <p className="text-[10px] text-gray-600 mt-1">
                                    10 digits starting with 6-9
                                </p>
                            </div>

                            {/* Care+ Price (carePrice) */}
                            <div>
                                <label className={LABEL}>
                                    Care+ Price (₹){" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <input
                                    className={INPUT}
                                    type="number"
                                    value={formData.carePrice}
                                    onChange={(e) =>
                                        setField("carePrice", e.target.value)
                                    }
                                    placeholder="900.00"
                                    step="0.01"
                                    min="0.01"
                                    required
                                />
                            </div>

                            {/* Brand (brand) */}
                            <div>
                                <label className={LABEL}>
                                    Brand{" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <input
                                    className={INPUT}
                                    type="text"
                                    value={formData.brand}
                                    onChange={(e) =>
                                        setField("brand", e.target.value)
                                    }
                                    placeholder="Converse"
                                    required
                                />
                            </div>

                            {/* Color (color) */}
                            <div>
                                <label className={LABEL}>
                                    Color{" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <input
                                    className={INPUT}
                                    type="text"
                                    value={formData.color}
                                    onChange={(e) =>
                                        setField("color", e.target.value)
                                    }
                                    placeholder="Blue"
                                    required
                                />
                            </div>

                            {/* Product Price (productPrice) */}
                            <div className="sm:col-span-2">
                                <label className={LABEL}>
                                    Product Price (₹){" "}
                                    <span className="text-red-400">*</span>
                                </label>
                                <input
                                    className={INPUT}
                                    type="text"
                                    value={formData.productPrice}
                                    onChange={(e) =>
                                        setField(
                                            "productPrice",
                                            e.target.value
                                        )
                                    }
                                    placeholder="9999.00"
                                    inputMode="decimal"
                                    required
                                />
                            </div>
                        </div>

                        {/* ── Optional Toggle ── */}
                        <button
                            type="button"
                            onClick={() => setShowOptional((s) => !s)}
                            className="w-full flex items-center justify-between px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-xs text-gray-400 hover:text-gray-200 hover:border-[#3d3d3d] transition-colors"
                        >
                            <span className="uppercase tracking-wide font-medium">
                                Optional Details
                            </span>
                            {showOptional ? (
                                <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                                <ChevronDown className="w-3.5 h-3.5" />
                            )}
                        </button>

                        {/* ── Optional Fields ── */}
                        {showOptional && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                                {/* Customer Name */}
                                <div>
                                    <label className={LABEL}>
                                        Customer Name
                                    </label>
                                    <input
                                        className={INPUT}
                                        value={formData.customerName}
                                        onChange={(e) =>
                                            setField(
                                                "customerName",
                                                e.target.value
                                            )
                                        }
                                        placeholder="John Doe"
                                    />
                                </div>

                                {/* Email */}
                                <div>
                                    <label className={LABEL}>Email</label>
                                    <input
                                        className={INPUT}
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) =>
                                            setField("email", e.target.value)
                                        }
                                        placeholder="john@example.com"
                                    />
                                </div>

                                {/* Address */}
                                <div className="sm:col-span-2">
                                    <label className={LABEL}>Address</label>
                                    <input
                                        className={INPUT}
                                        value={formData.address}
                                        onChange={(e) =>
                                            setField("address", e.target.value)
                                        }
                                        placeholder="123 Main St, City, State, ZIP"
                                    />
                                </div>

                                {/* Product Code (product) */}
                                <div>
                                    <label className={LABEL}>
                                        Product Code
                                    </label>
                                    <input
                                        className={INPUT}
                                        value={formData.product}
                                        onChange={(e) =>
                                            setField("product", e.target.value)
                                        }
                                        placeholder="CVL-BLU-06"
                                    />
                                </div>

                                {/* Size */}
                                <div>
                                    <label className={LABEL}>Size</label>
                                    <input
                                        className={INPUT}
                                        value={formData.size}
                                        onChange={(e) =>
                                            setField("size", e.target.value)
                                        }
                                        placeholder="6"
                                        inputMode="numeric"
                                    />
                                </div>

                                {/* Size Type */}
                                <div>
                                    <label className={LABEL}>Size Type</label>
                                    <input
                                        className={INPUT}
                                        value={formData.sizeType}
                                        onChange={(e) =>
                                            setField(
                                                "sizeType",
                                                e.target.value
                                            )
                                        }
                                        placeholder="UK / US / EU / IN / CM"
                                    />
                                </div>

                                {/* OPT IN (sold) */}
                                <div>
                                    <label className={LABEL}>OPT IN</label>
                                    <select
                                        className={INPUT}
                                        value={formData.sold}
                                        onChange={(e) =>
                                            setField("sold", e.target.value)
                                        }
                                    >
                                        <option value="true">True</option>
                                        <option value="false">False</option>
                                    </select>
                                </div>

                                {/* Gender */}
                                <div>
                                    <label className={LABEL}>Gender</label>
                                    <select
                                        className={INPUT}
                                        value={formData.gender}
                                        onChange={(e) =>
                                            setField("gender", e.target.value)
                                        }
                                    >
                                        <option value="-">—</option>
                                        <option value="M">Male</option>
                                        <option value="F">Female</option>
                                        <option value="Unisex">Unisex</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                {/* ── Footer ── */}
                <div className="px-5 py-3.5 border-t border-[#2d2d2d] flex justify-end gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={resetAndClose}
                        className="px-3 py-1.5 text-sm font-medium text-gray-400 hover:text-gray-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading && (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        )}
                        {loading ? "Creating…" : "Create Order"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ManualSaleForm;